import { ICoreContext } from '@msdyn365-commerce/core';
import { ProductPrice, SimpleProduct, UnitOfMeasureConversion } from "@msdyn365-commerce/retail-proxy";
import { humanizeUnitOfMeasure } from "../components/unit-of-measure";
import { IAppSettingsInContext } from '../settings/app.settings.autogenerated';
import { ProductSearchResultExt, SimpleProductExt } from './types';
import { getHideConversionInCategories } from '../settings/app.settings.utils';
import { isProductInCategory } from './common-utils';

export function getMostRelevantConversion(conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    // Accepted units, sorted by priority
    const acceptedToUnits = [
        'stk',
        'Stykk',
        'Stk-BS',
        'Kilogram',
        'kg',
        'Liter',
        '100_meter'
    ];

    for (const unit of acceptedToUnits) {
        const find = conversions?.find(conversion => conversion.ToUnitOfMeasureSymbol === unit);
        if (find) {
            return find;
        }
    }

    return undefined;
}

export function getKilogramConversion(defaultUnitOfMeasure: string | undefined, conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    if (!defaultUnitOfMeasure) {
        return undefined;
    }

    const acceptedToUnits = [
        'Kilogram',
        'kg'
    ];
    return conversions?.find(conversion =>
        conversion.FromUnitOfMeasureSymbol === defaultUnitOfMeasure &&
        conversion.ToUnitOfMeasureSymbol && acceptedToUnits.includes(conversion.ToUnitOfMeasureSymbol));
}

export function getLiterConversion(defaultUnitOfMeasure: string | undefined, conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    if (!defaultUnitOfMeasure) {
        return undefined;
    }

    const acceptedToUnits = [
        'Liter'
    ];
    return conversions?.find(conversion =>
        conversion.FromUnitOfMeasureSymbol === defaultUnitOfMeasure &&
        conversion.ToUnitOfMeasureSymbol && acceptedToUnits.includes(conversion.ToUnitOfMeasureSymbol));
}

export function getPcsConversion(conversions: UnitOfMeasureConversion[], fromUOM?: string): UnitOfMeasureConversion | undefined {
    const acceptedFromUnits = fromUOM ? [fromUOM] : [
        'Krt-M1',
        'Krt-BS',
        'Brett-M1',
        'Stk-M1'
    ];
    const acceptedToUnits = [
        'stk',
        'Stykk',
        'Stk-BS',
        'Pose-BS',
        'Flaske-BS',
        'Boks-BS',
        'Kanne-BS',
        'Krt-BS',
        'Sekk-BS',
        'Bunt-BS',
        'Fat-BS',
        'Glass-BS',
        'Rull-BS'
    ];

    return conversions?.find(conversion =>
        (conversion.FromUnitOfMeasureSymbol && acceptedFromUnits.includes(conversion.FromUnitOfMeasureSymbol)) &&
        (conversion.ToUnitOfMeasureSymbol && acceptedToUnits.includes(conversion.ToUnitOfMeasureSymbol)));
}

export function getKilogramForKilogramProduct(conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    const acceptedFromUnits = [
        'Kilogram',
        'kg'
    ];

    return conversions?.find(conversion => {
        return conversion.FromUnitOfMeasureSymbol?.endsWith('-BS') && acceptedFromUnits.includes(conversion.ToUnitOfMeasureSymbol || '');
    });
}

export function getLiterPrice(conversions: UnitOfMeasureConversion[], uom: string | undefined, price: number | ProductPrice): number | undefined {
    const literConversion = getLiterConversion(uom, conversions);

    // There is no liter conversion, so no price should be shown
    if (!literConversion || !literConversion.Factor) {
        return undefined;
    }

    const priceValue = typeof price === 'number' ? price : price.CustomerContextualPrice || 0;

    return priceValue / literConversion.Factor;
}

export function getConversionText(
    context: ICoreContext,
    uom: string | undefined,
    conversions: UnitOfMeasureConversion[],
    simpleProduct: SimpleProduct | undefined,
    breakoff: boolean,
    product?: ProductSearchResultExt | SimpleProductExt | undefined
): string | undefined {
    if (product && !product.FullyLoaded) {
        return undefined;
    }

    // Get conversions for pcs, liter, and kilogram
    const pcs = getPcsConversion(conversions, uom);
    const liter = getLiterConversion(uom, conversions);
    let kilogram = getKilogramConversion(uom, conversions);

    // Check if we should hide kg conversion for this product based on category settings
    const hideConversionInCategories = getHideConversionInCategories(context);
    const dontShowKgAndPcs = isProductInCategory(product, hideConversionInCategories);

    if (dontShowKgAndPcs && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1 && kilogram) {
        kilogram = undefined;
    }

    let result: string | undefined;

    // Special case: product has default UoM 'kg'
    if (simpleProduct?.DefaultUnitOfMeasure === 'kg') {
        const kg = getKilogramForKilogramProduct(conversions);
        if (kg) {
            return `ca ${kg.Factor || 0}kg per stk`;
        } else if (!breakoff && simpleProduct.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) {
            return `ca ${simpleProduct.Behavior.MultipleOfQuantity} kg`;
        }
    }

    // Override result if liter conversion exists
    if (liter) {
        const literPer = normalizeLiter((liter.Factor || 0) / (pcs?.Factor || 1));
        result = pcs
            ? `${pcs.Factor} x ${literPer}`
            : `${liter.Factor}l`;
    } else if (kilogram?.Factor) {
        result = pcs
            ? `${pcs.Factor} x ${normalizeKilogram(kilogram.Factor / (pcs.Factor || 1))}`
            : `${normalizeKilogram(kilogram.Factor)}`;
    } else if (pcs) {
        result = `${pcs.Factor} ${humanizeUnitOfMeasure(context, pcs.ToUnitOfMeasureSymbol)}`;
    }

    // Add total amounts of kilogram or liter
    if (result && !breakoff && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) {
        // Total amount for breakoff products
        result = `${simpleProduct.Behavior.MultipleOfQuantity} x ${result}`;

        if (liter?.Factor) {
            const totalLiter = Math.round((((liter.Factor || 0) * simpleProduct.Behavior.MultipleOfQuantity) + Number.EPSILON) * 100) / 100;
            result = `${result}, ${totalLiter}l`;
        } else if (kilogram?.Factor) {
            result += `, ${normalizeKilogram(kilogram.Factor * simpleProduct.Behavior.MultipleOfQuantity)}`;
        }
    } else {
        // Total amount for non breakoff products
        if (liter?.Factor && pcs?.Factor) {
            result += `, ${liter.Factor}l`;
        } else if (kilogram?.Factor && pcs?.Factor) {
            result += `, ${normalizeKilogram(kilogram.Factor)}`;
        }
    }

    // Fallback for special case UoMs ending with '-BS'
    if (!result && uom?.endsWith('-BS')) {
        const conv = conversions.find(x =>
            x.ItemId === simpleProduct?.ItemId &&
            x.ToUnitOfMeasureSymbol === uom &&
            x.FromUnitOfMeasureSymbol?.endsWith('-M1')
        );
        if (conv) {
            result = `${conv.Factor} per ${humanizeUnitOfMeasure(context, conv.FromUnitOfMeasureSymbol).toLocaleLowerCase()}`;
        }
    }

    return result;
}

/** Normalize liter value to 2 decimal places */
function normalizeLiter(liter: number): string {
    // Round to 2 decimals
    liter = Math.round((liter + Number.EPSILON) * 100) / 100;
    return `${liter}l`;
}

function normalizeKilogram(kilogram: number): string {
    let suffix = 'kg';
    if (kilogram < 1 && kilogram > 0) {
        // Show as grams if less than 1kg
        kilogram = kilogram * 1000;
        suffix = 'g';
    }
    // Round of factor to 2 decimals
    kilogram = Math.round((kilogram + Number.EPSILON) * 100) / 100;
    return `${kilogram}${suffix}`;
}

export function getNonBreakoffUOM(product: SimpleProduct | undefined, unitConversions: UnitOfMeasureConversion[] | undefined): string | undefined {
    if (!product || !unitConversions) {
        return undefined;
    }

    if (product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1 && product.DefaultUnitOfMeasure?.endsWith('-BS')) {
        const conversion = unitConversions.find(x =>
            x.ItemId === product.ItemId
            && x.ToUnitOfMeasureSymbol === product.DefaultUnitOfMeasure
            && x.FromUnitOfMeasureSymbol?.endsWith('-M1'));
        if (conversion) {
            return conversion.FromUnitOfMeasureSymbol;
        }
    }

    if (product.DefaultUnitOfMeasure === 'kg') {
        if (product.Behavior?.MultipleOfQuantity) {
            if (product.Behavior.MultipleOfQuantity > 0 && product.Behavior.MultipleOfQuantity < 1) {
                return 'Stk';
            } else {
                return 'Krt';
            }
        }
    }

    const liter = getLiterConversion(product.DefaultUnitOfMeasure, unitConversions);
    const kilogram = getKilogramConversion(product.DefaultUnitOfMeasure, unitConversions);

    if (!liter && !kilogram && product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1) {
        return 'Stk';
    }

    return undefined;
}

export function getRoundedFactor(conversion: UnitOfMeasureConversion): number {
    // Only round if the conversion is kg/Kilogram
    if (conversion.Factor && (conversion.ToUnitOfMeasureSymbol === 'kg' || conversion.ToUnitOfMeasureSymbol === 'Kilogram')) {
        return Math.round((conversion.Factor + Number.EPSILON) * 100) / 100;
    } else {
        return conversion.Factor || 0;
    }
}

export function getMinimumDecimals(unitOfMeasure: string | undefined): number {
    const units = [
        'kg',
        'Kilogram'
    ];
    return units.includes(unitOfMeasure || '') ? 2 : 0;
}

/**
 * Breakoff is only available for products sold in multiples when the configured
 * minimum order quantity does not itself require a full multiple.
 */
export function canBreakoffProduct(product: SimpleProduct | undefined): boolean {
    const multipleOfQuantity = product?.Behavior?.MultipleOfQuantity;
    const minimumQuantity = product?.Behavior?.MinimumQuantity;

    return !!multipleOfQuantity
        && multipleOfQuantity > 1
        && (minimumQuantity === undefined || minimumQuantity < multipleOfQuantity);
}

export function shouldShowBreakoff(context: ICoreContext<IAppSettingsInContext>, product: SimpleProduct | undefined, unitConversions: UnitOfMeasureConversion[] | undefined): boolean {
    if (!context.app.config?.disableBreakoffWhenConversionToSalesUnitFromBSExists) {
        return true;
    }

    if (!product || !unitConversions) {
        return false;
    }

    const conversion = unitConversions.find(x =>
        x.ItemId === product.ItemId
        && x.ToUnitOfMeasureSymbol === product.DefaultUnitOfMeasure
        && x.FromUnitOfMeasureSymbol?.endsWith('-BS'));

    return !conversion;
}

export function getTotalConversionText(
    context: ICoreContext,
    simpleProduct: SimpleProduct | undefined,
    unitConversions: UnitOfMeasureConversion[] | undefined,
    quantity: number,
    breakoff: boolean,
    product?: ProductSearchResultExt | SimpleProductExt | undefined
): string | undefined {
    if ((product && !product.FullyLoaded) || !simpleProduct || !unitConversions) {
        return undefined;
    }

    const liter = getLiterConversion(simpleProduct.DefaultUnitOfMeasure, unitConversions);
    let kilogram = getKilogramConversion(simpleProduct.DefaultUnitOfMeasure, unitConversions);

    // Check if we should hide kg conversion for this product based on category settings
    const hideConversionInCategories = getHideConversionInCategories(context);
    const dontShowKgAndPcs = isProductInCategory(product, hideConversionInCategories);

    if (dontShowKgAndPcs && simpleProduct.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1 && kilogram) {
        kilogram = undefined;
    }

    if (liter?.Factor) {
        const totalLiter = Math.round((liter.Factor * quantity + Number.EPSILON) * 100) / 100;

        if (breakoff) {
            return `${totalLiter} l`;
        } else {
            let adjustedQuantity = quantity;
            let uom = humanizeUnitOfMeasure(context, simpleProduct.DefaultUnitOfMeasure);

            if (simpleProduct.DefaultUnitOfMeasure?.endsWith('-BS') && !simpleProduct.Behavior?.MultipleOfQuantity) {
                return `${totalLiter} l`;
            }

            if (simpleProduct.DefaultUnitOfMeasure?.endsWith('-M1')) {
                const pcsConversion = getPcsConversion(unitConversions, simpleProduct.DefaultUnitOfMeasure);
                if (pcsConversion) {
                    adjustedQuantity = (pcsConversion.Factor || 1) * quantity;
                    adjustedQuantity = Math.round((adjustedQuantity + Number.EPSILON) * 100) / 100;
                    uom = humanizeUnitOfMeasure(context, pcsConversion.ToUnitOfMeasureSymbol);
                }
            }

            return `${adjustedQuantity} ${uom}\n${totalLiter} l`;
        }
    }

    if (kilogram?.Factor) {
        let factor = kilogram.Factor * quantity;
        let factorSuffix = 'kg';

        if (factor < 1 && factor > 0) {
            // Show as grams if less than 1kg
            factor = Math.round(factor * 1000);
            factorSuffix = 'g';
        }

        // Round of factor to 2 decimals
        factor = Math.round((factor + Number.EPSILON) * 100) / 100;

        if (breakoff) {
            return `${factor} ${factorSuffix}`;
        } else {
            let adjustedQuantity = quantity;
            let uom = humanizeUnitOfMeasure(context, simpleProduct.DefaultUnitOfMeasure);

            if (simpleProduct.DefaultUnitOfMeasure?.endsWith('-BS') && !simpleProduct.Behavior?.MultipleOfQuantity) {
                return `${factor} ${factorSuffix}`;
            }

            if (simpleProduct.DefaultUnitOfMeasure?.endsWith('-M1')) {
                const pcsConversion = getPcsConversion(unitConversions, simpleProduct.DefaultUnitOfMeasure);
                if (pcsConversion) {
                    adjustedQuantity = (pcsConversion.Factor || 1) * quantity;
                    adjustedQuantity = Math.round((adjustedQuantity + Number.EPSILON) * 100) / 100;
                    uom = humanizeUnitOfMeasure(context, pcsConversion.ToUnitOfMeasureSymbol);
                }
            }

            return `${adjustedQuantity} ${uom}\n${factor} ${factorSuffix}`;
        }
    }

    if (simpleProduct.DefaultUnitOfMeasure === 'kg') {
        if (simpleProduct.Behavior?.MultipleOfQuantity) {
            return `ca ${quantity} kg`;
        }
    }

    if (!breakoff && simpleProduct.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) {
        return `${quantity} ${humanizeUnitOfMeasure(context, simpleProduct.DefaultUnitOfMeasure)}`;
    }

    return undefined;
}
